import api from './api';

export interface WorkflowNode {
  id: string;
  type: string;
  position: { x: number; y: number };
  data: {
    label: string;
    type: string;
    category?: string;
    icon?: string;
    config: Record<string, any>;
    isConfigured?: boolean;
  };
}

export interface WorkflowEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
  type?: string;
}

export interface CRMIntegration {
  enabled: boolean;
  apiKey?: string;
  baseUrl?: string;
  projectId?: string;
  wabaId?: string;
  whatsappToken?: string;
}

export interface MarketplaceIntegration {
  enabled: boolean;
  apiKey?: string;
  baseUrl?: string;
  storeId?: string;
}

export interface CRMProject {
  id: string;
  name: string;
}

export interface Workflow {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  status: 'draft' | 'active' | 'paused' | 'error';
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  trigger_type?: string;
  trigger_config?: Record<string, any>;
  crm_integration?: CRMIntegration;
  marketplace_integration?: MarketplaceIntegration;
  executions_count: number;
  successful_executions: number;
  failed_executions: number;
  last_execution_at?: string;
  last_execution_status?: string;
  version: number;
  is_template: boolean;
  template_category?: string;
  created_at: string;
  updated_at: string;
}

export interface WorkflowExecution {
  id: string;
  workflow_id: string;
  user_id: string;
  lead_id?: string;
  lead_data?: Record<string, any>;
  trigger_data?: Record<string, any>;
  status: 'running' | 'completed' | 'failed' | 'paused' | 'waiting';
  current_node_id?: string;
  execution_log?: Array<{
    action: string;
    timestamp: string;
    [key: string]: any;
  }>;
  actions_executed: number;
  error_message?: string;
  started_at: string;
  completed_at?: string;
  resume_at?: string;
  created_at: string;
  updated_at: string;
  workflow?: {
    name: string;
  };
}

export interface WorkflowStats {
  totalWorkflows: number;
  activeWorkflows: number;
  totalExecutions: number;
  successfulExecutions: number;
  failedExecutions: number;
  successRate: number;
  recentExecutions: WorkflowExecution[];
}

export interface LandingPageOption {
  id: string;
  name: string;
  slug: string;
  status: string;
  published_url?: string;
  custom_domain?: string;
}

export interface CRMPipeline {
  id: string;
  name: string;
  stages?: Array<{
    id: string;
    name: string;
    order: number;
  }>;
}

// ===========================================
// WORKFLOW CRUD
// ===========================================

export const getWorkflows = async (params?: {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}) => {
  const response = await api.get('/automation/workflows', { params });
  return response.data;
};

export const getWorkflow = async (id: string): Promise<{ success: boolean; data: Workflow }> => {
  const response = await api.get(`/automation/workflows/${id}`);
  return response.data;
};

export const createWorkflow = async (data: Partial<Workflow>): Promise<{ success: boolean; data: Workflow }> => {
  const response = await api.post('/automation/workflows', data);
  return response.data;
};

export const updateWorkflow = async (
  id: string,
  data: Partial<Workflow>
): Promise<{ success: boolean; data: Workflow }> => {
  const response = await api.put(`/automation/workflows/${id}`, data);
  return response.data;
};

export const updateWorkflowStatus = async (
  id: string,
  status: 'draft' | 'active' | 'paused'
): Promise<{ success: boolean; data: Workflow }> => {
  const response = await api.patch(`/automation/workflows/${id}/status`, { status });
  return response.data;
};

export const deleteWorkflow = async (id: string): Promise<{ success: boolean }> => {
  const response = await api.delete(`/automation/workflows/${id}`);
  return response.data;
};

export const duplicateWorkflow = async (id: string): Promise<{ success: boolean; data: Workflow }> => {
  const response = await api.post(`/automation/workflows/${id}/duplicate`);
  return response.data;
};

// ===========================================
// WORKFLOW EXECUTION
// ===========================================

export const executeWorkflow = async (
  id: string,
  testData?: Record<string, any>
): Promise<{ success: boolean; data: { executionId: string; status: string } }> => {
  const response = await api.post(`/automation/workflows/${id}/execute`, { testData });
  return response.data;
};

export const getWorkflowExecutions = async (
  workflowId: string,
  params?: { page?: number; limit?: number; status?: string }
): Promise<{ success: boolean; data: WorkflowExecution[]; pagination: any }> => {
  const response = await api.get(`/automation/workflows/${workflowId}/executions`, { params });
  return response.data;
};

// ===========================================
// WORKFLOW STATS
// ===========================================

export const getWorkflowStats = async (): Promise<{ success: boolean; data: WorkflowStats }> => {
  const response = await api.get('/automation/workflows/stats');
  return response.data;
};

// ===========================================
// TEMPLATES
// ===========================================

export const getWorkflowTemplates = async (category?: string): Promise<{
  success: boolean;
  data: {
    system: Workflow[];
    user: Workflow[];
  };
}> => {
  const response = await api.get('/automation/workflows/templates', {
    params: { category }
  });
  return response.data;
};

export const createWorkflowFromTemplate = async (
  templateId: string,
  name?: string
): Promise<{ success: boolean; data: Workflow }> => {
  const response = await api.post('/automation/workflows/from-template', {
    templateId,
    name
  });
  return response.data;
};

// ===========================================
// CRM INTEGRATION
// ===========================================

export const testCrmConnection = async (
  apiKey: string
): Promise<{ success: boolean; message: string; data?: any; error?: string }> => {
  const response = await api.post('/automation/workflows/test-crm', {
    apiKey
  });
  return response.data;
};

export const getCrmPipelines = async (
  apiKey: string
): Promise<{ success: boolean; data: CRMPipeline[] }> => {
  const response = await api.get('/automation/workflows/crm-pipelines', {
    params: { apiKey }
  });
  return response.data;
};

export const getCrmProjects = async (
  apiKey: string
): Promise<{ success: boolean; data: CRMProject[] }> => {
  const response = await api.get('/automation/workflows/crm-projects', {
    params: { apiKey }
  });
  return response.data;
};

export interface CRMTag {
  id: string;
  name: string;
  color?: string;
}

export const getCrmTags = async (
  apiKey: string
): Promise<{ success: boolean; data: CRMTag[] }> => {
  const response = await api.get('/automation/workflows/crm-tags', {
    params: { apiKey }
  });
  return response.data;
};

// ===========================================
// WHATSAPP BUSINESS TEMPLATES
// ===========================================

export interface WhatsAppTemplate {
  id: string;
  name: string;
  language: string;
  category: string;
  status: string;
  components?: Array<{
    type: 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS';
    format?: string;
    text?: string;
    parameters?: Array<{ type: string; text?: string }>;
    buttons?: Array<{ type: string; text: string; url?: string; phone_number?: string }>;
  }>;
}

export interface CRMCustomField {
  id: string;
  key: string;
  name: string;
  type: string;
  entityType: string;
}

export const getCrmCustomFields = async (
  apiKey: string
): Promise<{ success: boolean; data: CRMCustomField[] }> => {
  const response = await api.get('/automation/workflows/crm-custom-fields', {
    params: { apiKey }
  });
  return response.data;
};

export const getWhatsAppTemplates = async (
  apiKey?: string,
  wabaId?: string,
  whatsappToken?: string
): Promise<{ success: boolean; data: WhatsAppTemplate[] }> => {
  const response = await api.get('/automation/workflows/whatsapp-templates', {
    params: { apiKey, wabaId, whatsappToken }
  });
  return response.data;
};

// ===========================================
// LANDING PAGES (for trigger configuration)
// ===========================================

export const getLandingPagesForWorkflow = async (): Promise<{
  success: boolean;
  data: LandingPageOption[];
}> => {
  const response = await api.get('/automation/workflows/landing-pages');
  return response.data;
};

// ===========================================
// AI WORKFLOW GENERATION
// ===========================================

export interface AIGeneratedWorkflow {
  nodes: Array<{
    id: string;
    type: string;
    nodeType: string;
    label: string;
    icon: string;
    position: { x: number; y: number };
    config: Record<string, any>;
  }>;
  edges: Array<{
    id: string;
    source: string;
    target: string;
    sourceHandle?: string;
    targetHandle?: string;
  }>;
  summary: string;
}

export const generateWorkflowWithAI = async (
  prompt: string,
  useAsync = true,
  hasMarketplace = false
): Promise<{ success: boolean; data?: AIGeneratedWorkflow; tokensUsed?: number; model?: string; async?: boolean; jobId?: string }> => {
  const response = await api.post('/automation/workflows/generate-ai', { prompt, async: useAsync, hasMarketplace });
  return response.data;
};

// ===========================================
// HELPER FUNCTIONS
// ===========================================

export const getNodeCategories = () => [
  {
    id: 'triggers',
    name: 'Gatilhos',
    description: 'Iniciam o workflow quando um evento acontece',
    color: 'emerald'
  },
  {
    id: 'actions',
    name: 'Acoes',
    description: 'Executam acoes como enviar emails ou mensagens',
    color: 'blue'
  },
  {
    id: 'crm',
    name: 'CRM',
    description: 'Integram com o sistema CRM',
    color: 'purple'
  },
  {
    id: 'logic',
    name: 'Logica',
    description: 'Controlam o fluxo com condicoes e delays',
    color: 'orange'
  }
];

export const getNodeTypes = () => ({
  triggers: [
    {
      type: 'trigger_landing_page',
      label: 'Lead de Landing Page',
      icon: 'FileText',
      description: 'Dispara quando um lead preenche formulario da LP'
    },
    {
      type: 'trigger_form',
      label: 'Formulario',
      icon: 'ClipboardList',
      description: 'Dispara quando um formulario e enviado'
    },
    {
      type: 'trigger_webhook',
      label: 'Webhook',
      icon: 'Webhook',
      description: 'Dispara quando um webhook e recebido'
    },
    {
      type: 'trigger_manual',
      label: 'Manual',
      icon: 'Play',
      description: 'Dispara manualmente para testes'
    },
    {
      type: 'trigger_instagram_comment',
      label: 'Comentario no Instagram',
      icon: 'Instagram',
      description: 'Dispara quando alguem comenta em uma publicacao do Instagram'
    },
    {
      type: 'trigger_facebook_comment',
      label: 'Comentario no Facebook',
      icon: 'Facebook',
      description: 'Dispara quando alguem comenta em uma publicacao do Facebook'
    },
    {
      type: 'trigger_threads_comment',
      label: 'Comentario no Threads',
      icon: 'AtSign',
      description: 'Dispara quando alguem comenta em uma publicacao do Threads'
    },
    {
      type: 'trigger_instagram_first_dm',
      label: 'Primeira DM no Instagram',
      icon: 'Instagram',
      description: 'Dispara quando alguem envia a primeira mensagem direta para sua conta'
    },
    {
      type: 'trigger_instagram_icebreaker',
      label: 'Ice Breaker no Instagram',
      icon: 'Instagram',
      description: 'Dispara quando alguem clica em um botao de Ice Breaker no seu DM'
    }
  ],
  actions: [
    {
      type: 'send_email',
      label: 'Enviar Email',
      icon: 'Mail',
      description: 'Envia um email para o lead'
    },
    {
      type: 'send_whatsapp',
      label: 'Enviar WhatsApp',
      icon: 'MessageCircle',
      description: 'Envia mensagem no WhatsApp'
    },
    {
      type: 'send_sms',
      label: 'Enviar SMS',
      icon: 'Smartphone',
      description: 'Envia SMS para o lead'
    },
    {
      type: 'schedule_call',
      label: 'Agendar Ligacao',
      icon: 'Phone',
      description: 'Agenda uma ligacao no CRM'
    },
    {
      type: 'send_notification',
      label: 'Notificacao Interna',
      icon: 'Bell',
      description: 'Envia notificacao para a equipe'
    },
    {
      type: 'reply_comment',
      label: 'Responder Comentario',
      icon: 'MessageCircle',
      description: 'Responde o comentario na publicacao (Instagram/Facebook)'
    },
    {
      type: 'send_social_dm',
      label: 'Enviar DM',
      icon: 'Send',
      description: 'Envia mensagem direta ao autor do comentario (Instagram/Facebook)'
    },
    {
      type: 'reply_welcome_dm',
      label: 'Responder DM de Boas-Vindas',
      icon: 'MessageSquare',
      description: 'Envia mensagem de boas-vindas em resposta a primeira DM recebida'
    },
    {
      type: 'reply_icebreaker',
      label: 'Responder Ice Breaker',
      icon: 'MessageSquare',
      description: 'Envia resposta quando alguem clica em um botao de Ice Breaker'
    }
  ],
  crm: [
    {
      type: 'crm_create_lead',
      label: 'Criar Lead no CRM',
      icon: 'UserPlus',
      description: 'Cria um novo lead no sistema CRM'
    },
    {
      type: 'crm_update_lead',
      label: 'Atualizar Lead',
      icon: 'Edit',
      description: 'Atualiza dados do lead no CRM'
    },
    {
      type: 'crm_change_stage',
      label: 'Mudar Estagio',
      icon: 'ArrowRight',
      description: 'Move o lead para outro estagio do pipeline'
    },
    {
      type: 'crm_add_tag',
      label: 'Adicionar Tag',
      icon: 'Tag',
      description: 'Adiciona uma tag ao lead'
    },
    {
      type: 'crm_assign',
      label: 'Atribuir Responsavel',
      icon: 'User',
      description: 'Atribui o lead a um responsavel'
    },
    {
      type: 'crm_update_score',
      label: 'Atualizar Score',
      icon: 'TrendingUp',
      description: 'Atualiza o score/pontuacao do lead'
    },
    {
      type: 'crm_add_note',
      label: 'Adicionar Nota',
      icon: 'FileText',
      description: 'Adiciona uma nota ao historico do lead'
    }
  ],
  logic: [
    {
      type: 'condition',
      label: 'Condicao If/Else',
      icon: 'GitBranch',
      description: 'Divide o fluxo baseado em uma condicao'
    },
    {
      type: 'wait',
      label: 'Aguardar Tempo',
      icon: 'Clock',
      description: 'Espera um tempo antes de continuar'
    },
    {
      type: 'wait_response',
      label: 'Aguardar Resposta',
      icon: 'MessageSquare',
      description: 'Espera a resposta do lead'
    },
    {
      type: 'ab_test',
      label: 'Teste A/B',
      icon: 'Split',
      description: 'Divide o trafego para teste A/B'
    }
  ]
});

export default {
  getWorkflows,
  getWorkflow,
  createWorkflow,
  updateWorkflow,
  updateWorkflowStatus,
  deleteWorkflow,
  duplicateWorkflow,
  executeWorkflow,
  getWorkflowExecutions,
  getWorkflowStats,
  getWorkflowTemplates,
  createWorkflowFromTemplate,
  testCrmConnection,
  getCrmPipelines,
  getCrmProjects,
  getCrmTags,
  getCrmCustomFields,
  getWhatsAppTemplates,
  getLandingPagesForWorkflow,
  getNodeCategories,
  getNodeTypes,
  generateWorkflowWithAI
};
